import * as React from 'react';
import styled, {css} from 'styled-components';
import {omit} from 'lodash';
import {transition} from 'helpers/css';
import {transparentize} from 'polished';

export interface IconBoxProps {
  size: number;
  active?: boolean;
  shadow?: boolean;
  noBorder?: boolean;
  defaultHidden?: boolean;
  icon: React.ReactElement;
}

const Container = styled.div<Pick<IconBoxProps,
  | 'size'
  | 'active'
  | 'shadow'
  | 'noBorder'
  | 'defaultHidden'>>`
  
  padding: 0;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 2px solid #454548;

  ${({noBorder, theme}) => {
    return noBorder && css`
      color: ${theme.variables.color};
      border-color: transparent;
    `;
  }}

  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
  
  ${({active, theme}) => {
    return active && css`
      color: ${theme.variables.color};
      border-color: transparent;
    `;
  }}
  
  background: ${({theme, active}) => active
    ? theme.colors.primary
    : transparentize(.95, theme.variables.color)};
  
  ${({size}) => {
    return css`
      width: ${size}px;
      height: ${size}px;
    `;
  }}
  
  ${({shadow, active}) => {
    return active && shadow && css`
      box-shadow: 2px 9px 14px rgba(50, 100, 239, 0.4);
    `;
  }}

  ${transition()};
  
  &:hover {
    cursor: ${({defaultHidden}) => defaultHidden && 'pointer'};
    border-color: ${({active}) => !active && '#454548'};
    & > svg {
      ${({defaultHidden, active}) => {
        return !active && defaultHidden && css`
          opacity: .3;
      `;
  }}
    }
  }
  
  svg {
    width: 12px;
    height: 12px;
    
    ${transition()};

    ${({defaultHidden, active}) => {
    return !active && defaultHidden && css`
      opacity: 0;
    `;
  }}
  }
`;

/**
 * This component renders course schedule box with icon
 * @param {IconBoxProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function IconBox(props: IconBoxProps): React.ReactElement {
  const containerProps = omit(props, 'icon');
  return (
    <Container {...containerProps}>
      {props.icon}
    </Container>
  );
}
